
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useIsMobile } from '@/hooks/use-mobile';
import { MainContent } from './components/MainContent';
import { MobileSidebar } from './components/MobileSidebar';
import { ChatButton } from '@/components/chat/ChatButton';
import GlobalComposeOverlay from '@/components/email/layout/GlobalComposeOverlay';

interface AppLayoutProps {
  children?: React.ReactNode;
}

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed-state';

const AppLayout = ({ children }: AppLayoutProps) => {
  const isMobile = useIsMobile();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Initialize sidebar collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_STORAGE_KEY);
      return saved ? JSON.parse(saved) : false;
    } catch (error) {
      console.warn('Failed to load sidebar state from localStorage:', error);
      return false;
    }
  });
  
  const location = useLocation();
  
  useEffect(() => {
    if (isMobile && sidebarOpen) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  const handleMenuClick = () => {
    setSidebarOpen(prev => !prev);
  };

  const toggleSidebarCollapse = () => {
    setSidebarCollapsed(prev => {
      const newState = !prev;
      // Save to localStorage
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(newState));
      } catch (error) {
        console.warn('Failed to save sidebar state to localStorage:', error);
      }
      return newState;
    });
  };

  // Hide chat button on the chat page
  const shouldShowChatButton = location.pathname !== '/app/chat';

  return (
    <div className="flex min-h-screen w-full bg-background">
      {/* Desktop sidebar */}
      {!isMobile && (
        <Sidebar 
          collapsed={sidebarCollapsed} 
          onToggleCollapse={toggleSidebarCollapse}
        />
      )}
      
      {/* Mobile sidebar */}
      {isMobile && (
        <MobileSidebar 
          open={sidebarOpen} 
          onOpenChange={setSidebarOpen} 
        />
      )}
      
      <MainContent 
        onMenuClick={handleMenuClick}
        isMobile={isMobile}
        onToggleSidebar={toggleSidebarCollapse}
        sidebarCollapsed={sidebarCollapsed}
      >
        {children}
        
        {/* Floating UI elements */}
        {shouldShowChatButton && <ChatButton />}
      </MainContent>

      {/* Global email composer — openable from anywhere via useComposeEmail() */}
      <GlobalComposeOverlay />
    </div>
  );
};

export default AppLayout;
